/**
 * 📞 Página de Contato/Suporte
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Phone, Mail, MessageCircle, Clock, MapPin, Send, 
  ArrowLeft, HeadphonesIcon, CheckCircle, AlertCircle 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contato = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    telefone: "",
    assunto: "",
    mensagem: ""
  });

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome || !formData.email || !formData.mensagem) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact/send`, formData);
      setSent(true);
      toast.success("Mensagem enviada com sucesso!");
    } catch (error) {
      toast.error("Erro ao enviar mensagem. Tente novamente ou entre em contato pelo WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const whatsappNumber = "5517996462254";
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=Olá! Preciso de suporte com o sistema JsSolution.`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-600 hover:text-emerald-600 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Voltar</span>
          </button>
          
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <MessageCircle className="w-5 h-5" />
            <span className="hidden sm:inline">WhatsApp</span>
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-100 mb-6">
            <HeadphonesIcon className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            Central de Suporte
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Estamos aqui para ajudar! Entre em contato conosco por qualquer um dos canais abaixo.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="pb-8 px-4">
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
          {/* WhatsApp Card */}
          <Card className="border-emerald-200 hover:shadow-lg transition-shadow cursor-pointer group"
                onClick={() => window.open(whatsappLink, '_blank')}>
            <CardContent className="p-6 text-center">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4 group-hover:bg-emerald-500 transition-colors">
                <MessageCircle className="w-7 h-7 text-emerald-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-bold text-lg text-slate-900 mb-2">WhatsApp</h3>
              <p className="text-emerald-600 font-semibold text-lg">(17) 99646-2254</p>
              <p className="text-slate-500 text-sm mt-2">Atendimento rápido</p>
            </CardContent>
          </Card>

          {/* Phone Card */}
          <Card className="border-blue-200 hover:shadow-lg transition-shadow cursor-pointer group"
                onClick={() => window.open('tel:+5517996462254', '_blank')}>
            <CardContent className="p-6 text-center">
              <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-500 transition-colors">
                <Phone className="w-7 h-7 text-blue-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-bold text-lg text-slate-900 mb-2">Telefone</h3>
              <p className="text-blue-600 font-semibold text-lg">(17) 99646-2254</p>
              <p className="text-slate-500 text-sm mt-2">Ligação direta</p>
            </CardContent>
          </Card>

          {/* Hours Card */}
          <Card className="border-orange-200 hover:shadow-lg transition-shadow">
            <CardContent className="p-6 text-center">
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
                <Clock className="w-7 h-7 text-orange-600" />
              </div>
              <h3 className="font-bold text-lg text-slate-900 mb-2">Horário</h3>
              <p className="text-orange-600 font-semibold">Seg - Sex</p>
              <p className="text-slate-500 text-sm mt-2">8h às 18h</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <Card>
            <CardHeader className="text-center border-b">
              <CardTitle className="flex items-center justify-center gap-2 text-2xl">
                <Mail className="w-6 h-6 text-emerald-600" />
                Envie uma Mensagem
              </CardTitle>
              <p className="text-slate-600 mt-2">
                Preencha o formulário abaixo e retornaremos o mais breve possível
              </p>
            </CardHeader>
            <CardContent className="p-6">
              {sent ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-emerald-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Mensagem Enviada!</h3>
                  <p className="text-slate-600 mb-6">
                    Recebemos sua mensagem e entraremos em contato em breve.
                  </p>
                  <div className="flex gap-4 justify-center">
                    <Button 
                      variant="outline"
                      onClick={() => {
                        setSent(false);
                        setFormData({ nome: "", email: "", telefone: "", assunto: "", mensagem: "" });
                      }}
                    >
                      Enviar outra mensagem
                    </Button>
                    <Button onClick={() => navigate("/")}>
                      Voltar ao início
                    </Button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Nome Completo *
                      </label>
                      <Input
                        value={formData.nome}
                        onChange={(e) => handleChange("nome", e.target.value)}
                        placeholder="Seu nome"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Email *
                      </label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange("email", e.target.value)}
                        placeholder="seu@email.com"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Telefone
                      </label>
                      <Input
                        value={formData.telefone}
                        onChange={(e) => handleChange("telefone", e.target.value)}
                        placeholder="(00) 00000-0000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Assunto
                      </label>
                      <Input
                        value={formData.assunto}
                        onChange={(e) => handleChange("assunto", e.target.value)}
                        placeholder="Ex: Dúvida sobre o sistema"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Mensagem *
                    </label>
                    <Textarea
                      value={formData.mensagem}
                      onChange={(e) => handleChange("mensagem", e.target.value)}
                      placeholder="Descreva sua dúvida, problema ou sugestão..."
                      rows={5}
                      required
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-lg py-6"
                  >
                    {loading ? (
                      "Enviando..."
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Enviar Mensagem
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ Quick Links */}
      <section className="py-12 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Dúvidas Frequentes</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="text-left hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <h4 className="font-medium text-slate-900">Como funciona o período de teste?</h4>
                  <p className="text-sm text-slate-600 mt-1">
                    Você tem acesso a todos os módulos durante o período trial gratuitamente.
                  </p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="text-left hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <h4 className="font-medium text-slate-900">Como faço para contratar?</h4>
                  <p className="text-sm text-slate-600 mt-1">
                    Acesse sua conta e escolha os módulos que deseja na área de assinatura.
                  </p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="text-left hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <h4 className="font-medium text-slate-900">O sistema emite nota fiscal?</h4>
                  <p className="text-sm text-slate-600 mt-1">
                    Sim! Com o módulo fiscal você emite NFC-e direto pelo sistema.
                  </p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="text-left hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <h4 className="font-medium text-slate-900">Posso usar em vários dispositivos?</h4>
                  <p className="text-sm text-slate-600 mt-1">
                    Sim, o sistema é 100% web e funciona em qualquer dispositivo.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-200">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-slate-600">
            © {new Date().getFullYear()} JsSolution. Todos os direitos reservados.
          </p>
          <div className="flex items-center justify-center gap-4 mt-4">
            <a href="/admin/login" className="text-emerald-600 hover:text-emerald-700">
              Acessar Sistema
            </a>
            <span className="text-slate-300">|</span>
            <a href="/registrar" className="text-emerald-600 hover:text-emerald-700">
              Criar Conta
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Contato;
